import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardRedirector from "./components/DashboardRedirector";
import NavigationProvider from "./contexts/NavigationContext";
import NavigationLayout from "./components/layout/NavigationLayout";
import NavigationAnalytics from "./components/navigation/NavigationAnalytics";
import FloatingChatBubble from "./components/messaging/FloatingChatBubble";
import OnboardingGuide from './components/va/OnboardingGuide';
import EmployerOnboardingGuide from './components/employer/EmployerOnboardingGuide';
import { useAuth } from './hooks/useAuth';
// Add your imports here
import LoginRegisterWrapper from "./pages/login-register/LoginRegisterWrapper";
import ResetPassword from "./pages/auth/ResetPassword";
import VerifyEmail from "./pages/auth/verify";
import VerifyEmailChange from "./pages/auth/verify-email-change";
import AuthCallback from "./pages/auth/AuthCallback";
import VADashboard from "./pages/va-dashboard";
import UpSkillsPage from "./pages/up-skills";
import SkillsAssessment from "./pages/skills-assessment";
// Removed MessagesCommunication import as part of messaging consolidation
import InterviewsPage from './pages/interviews';
import AnalyticsPage from './pages/analytics';
import EmployerDashboard from "./pages/employer-dashboard";
import EmployerOnboarding from "./pages/employer-onboarding";
import EmployerActivitiesPage from "./pages/employer-dashboard/EmployerActivitiesPage";
import InterviewResults from "./pages/employer-dashboard/pages/InterviewResults";
import EmployerProfileSettings from "./pages/employer-profile-settings";
import JobPostingPage from "./pages/job-posting";
import FormBuilder from "./pages/form-builder";
import CandidateSearchPage from "./pages/candidate-search";
import PublicCandidateSearch from "./pages/candidate-search/PublicCandidateSearch";
import InterviewManagementPage from './pages/interview-management';
import TeamManagementPage from './pages/team-management';
import AdvancedAnalyticsPage from './pages/advanced-analytics';
import AIMatchingPage from './pages/auto-matching';
import EnterpriseFeaturesPage from './pages/enterprise-features';
// Import the messaging components
import MessagingPageWrapper from './components/messaging/MessagingPageWrapper';
import SecureConversationRoute from './components/messaging/SecureConversationRoute';

import TimeTrackerPage from './pages/time-tracker/TimeTrackerPage';
import VAProfile from "./pages/va-profile";
import VAProfileSidebar from "./pages/va-profile/va-profile-sidebar";
import VAProfileSettings from "./pages/va-profile-settings";
import VAPortfolioManagement from "./pages/va-portfolio-management";
import JobSearch from "./pages/job-search";
import JobDetails from "./pages/job-details";
import JobApplication from "./pages/job-application";

import ApplicationsPage from "./pages/applications";
import SavedJobs from "./pages/saved-jobs";
import NotFound from "./pages/NotFound";
import VAOnboarding from './pages/va-onboarding';
import AdminModerationPage from './pages/admin-moderation';
import FeedbackDashboard from './pages/admin/FeedbackDashboard';
import AdminSupportDashboard from './pages/admin/support/AdminSupportDashboard';
import ModeratorChatDashboard from './pages/moderator/chat/ModeratorChatDashboard';
import HelpArticlesCMS from './pages/admin/help-cms';
import ArticleEditor from './pages/admin/help-cms/ArticleEditor';
import AdminProfileSettings from './pages/admin/admin-profile-settings';
import PublicVAProfile from "./pages/PublicVAProfile";
import VALookup from "./pages/VALookup";
import ContactVAForm from "./pages/ContactVAForm";
import NotificationsPage from "./pages/notifications";
import HelpSupportWrapper from "./pages/help-support/HelpSupportWrapper";
import ArticleDetailPage from "./pages/help-support/ArticleDetailPage";
import VATimesheetPage from "./pages/va-timesheet";
import HowItWorksPage from "./pages/how-it-works";
import SkillsDirectoryPage from "./pages/skills-directory";
import VAActivitiesPage from "./pages/va-activities";
import PricingPage from "./pages/PricingPage";
import PaymentSuccessPage from "./pages/payment/PaymentSuccessPage";
import CommunityGuidelinesPage from "./pages/community-guidelines";
import PrivacyPolicyPage from "./pages/privacy-policy";
import TermsOfServicePage from "./pages/terms-of-service";
import CookiesPolicyPage from "./pages/cookies-policy";
import Sitemap from "./pages/sitemap";

const Routes = () => {
  const { userProfile } = useAuth();

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ErrorBoundary>
        <NavigationProvider>
          <NavigationLayout>
            <RouterRoutes>
              {/* Public Routes */}
              <Route path="/" element={<LoginRegisterWrapper />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/skills-directory" element={<SkillsDirectoryPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/cookies-policy" element={<CookiesPolicyPage />} />
              <Route path="/sitemap" element={<Sitemap />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/login-register" element={<LoginRegisterWrapper />} />
              <Route path="/auth/verify" element={<VerifyEmail />} />
              <Route path="/auth/verify-email-change" element={<VerifyEmailChange />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              {/* Secure conversation route - handles URL parameters */}
              <Route path="/messages/:participantSlug/:jobId?" element={<SecureConversationRoute />} />
              {/* Admin routes */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/va/:username" element={<PublicVAProfile />} />
              <Route path="/va-lookup/:id" element={<VALookup />} />
              <Route path="/browse-candidates" element={<PublicCandidateSearch />} />
              
          {/* Protected Routes - VA Only */}
          <Route
            path="/va-dashboard"
            element={
              <ProtectedRoute requiredRole="va">
                <VADashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/skills-assessment"
            element={
              <ProtectedRoute requiredRole="va">
                <SkillsAssessment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/up-skills"
            element={
              <ProtectedRoute requiredRole="va">
                <UpSkillsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/va-profile"
            element={
              <ProtectedRoute requiredRole="va">
                <VAProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/va-profile-sidebar"
            element={
              <ProtectedRoute requiredRole="va">
                <VAProfileSidebar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/va-profile-settings"
            element={
              <ProtectedRoute requiredRole="va">
                <VAProfileSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/va-portfolio-management"
            element={
              <ProtectedRoute requiredRole="va">
                <VAPortfolioManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/va-onboarding"
            element={
              <ProtectedRoute requiredRole="va">
                <VAOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications"
            element={
              <ProtectedRoute requiredRole="va">
                <ApplicationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-contracts"
            element={
              <ProtectedRoute requiredRole="va">
                <VATimesheetPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activities"
            element={
              <ProtectedRoute requiredRole="va">
                <VAActivitiesPage />
              </ProtectedRoute>
            }
          />
          
          {/* Contact VA Form - Protected for Employers */}
          <Route
            path="/contact-va/:id"
            element={
              <ProtectedRoute requiredRole="employer">
                <ContactVAForm />
              </ProtectedRoute>
            }
          />
          
          {/* Protected Routes - Employer Only */}
          <Route
            path="/employer-onboarding"
            element={
              <ProtectedRoute requiredRole="employer">
                <EmployerOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer-dashboard"
            element={
              <ProtectedRoute requiredRole="employer">
                <EmployerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer-dashboard/activities"
            element={
              <ProtectedRoute requiredRole="employer">
                <EmployerActivitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer-dashboard/interviews"
            element={
              <ProtectedRoute requiredRole="employer">
                <InterviewResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer-profile-settings"
            element={
              <ProtectedRoute requiredRole="employer">
                <EmployerProfileSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/job-posting"
            element={
              <ProtectedRoute requiredRole="employer">
                <JobPostingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/job-posting/:jobId"
            element={
              <ProtectedRoute requiredRole="employer">
                <JobPostingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/form-builder"
            element={
              <ProtectedRoute requiredRole="employer">
                <FormBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/form-builder/:formId"
            element={
              <ProtectedRoute requiredRole="employer">
                <FormBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate-search"
            element={
              <ProtectedRoute requiredRole="employer">
                <CandidateSearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/interview-management"
            element={
              <ProtectedRoute requiredRole="employer">
                <InterviewManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team-management"
            element={
              <ProtectedRoute allowedRoles={['employer']}>
                <TeamManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/advanced-analytics"
            element={
              <ProtectedRoute allowedRoles={['employer']}>
                <AdvancedAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auto-matching"
            element={
              <ProtectedRoute allowedRoles={['employer']}>
                <AIMatchingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/enterprise-features"
            element={
              <ProtectedRoute allowedRoles={['employer']}>
                <EnterpriseFeaturesPage />
              </ProtectedRoute>
            }
          />
          
          {/* Protected Routes - Admin and Moderator Only */}
          <Route
            path="/admin-moderation"
            element={
              <ProtectedRoute allowedRoles={['admin', 'moderator']}>
                <AdminModerationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-moderation"
            element={<Navigate to="/admin-moderation?tab=job-moderation" replace />}
          />
          <Route
            path="/admin/feedback"
            element={<Navigate to="/admin-moderation?tab=feedback" replace />}
          />
          <Route
            path="/admin/upgrade-analytics"
            element={<Navigate to="/admin-moderation?tab=analytics" replace />}
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSupportDashboard />
              </ProtectedRoute>
            }
          />
          {/* Help CMS routes - now integrated into admin-moderation but kept for backward compatibility */}
          <Route
            path="/admin/help-cms"
            element={<Navigate to="/admin-moderation?tab=help-cms" replace />}
          />
          <Route
            path="/admin/help-cms/create"
            element={<Navigate to="/admin-moderation?tab=help-cms&mode=create" replace />}
          />
          <Route
            path="/admin/help-cms/edit/:articleId"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ArticleEditor />
              </ProtectedRoute>
            }
          />

          {/* Moderator Chat Dashboard */}
          <Route
            path="/moderator/chat"
            element={
              <ProtectedRoute allowedRoles={['moderator']}>
                <ModeratorChatDashboard />
              </ProtectedRoute>
            }
          />

          {/* Admin Profile Settings Route */}
          <Route
            path="/admin/profile-settings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'moderator']}>
                <AdminProfileSettings />
              </ProtectedRoute>
            }
          />
          
          {/* Protected Routes - Both Roles */}
          <Route 
            path="/messages" 
            element={
              <ProtectedRoute>
                <MessagingPageWrapper />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/messages/with/:participantSlug" 
            element={
              <ProtectedRoute>
                <SecureConversationRoute />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/time-tracker/:vaName" 
            element={
              <ProtectedRoute>
                <TimeTrackerPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/interviews" 
            element={
              <ProtectedRoute requiredRole="va">
                <InterviewsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/analytics" 
            element={
              <ProtectedRoute requiredRole="va">
                <AnalyticsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/notifications" 
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/help-support" 
            element={<HelpSupportWrapper />}
          />
          <Route 
            path="/help-support/:slug" 
            element={
              <ProtectedRoute>
                <ArticleDetailPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/job-search"
            element={<JobSearch />}
          />
          <Route
            path="/saved-jobs"
            element={
              <ProtectedRoute>
                <SavedJobs />
              </ProtectedRoute>
            }
          />
          {/* Job Details - Support both slug and UUID formats */}
          <Route
            path="/job-details/:jobSlug"
            element={<JobDetails />}
          />
          {/* Job Details with query parameter */}
          <Route
            path="/job-details"
            element={<JobDetails />}
          />
          {/* Legacy support for direct UUID access */}
          <Route
            path="/job/:jobId"
            element={<JobDetails />}
          />
          {/* Job Application Page - for jobs with forms */}
          <Route
            path="/job-application/:jobSlug"
            element={
              <ProtectedRoute>
                <JobApplication />
              </ProtectedRoute>
            }
          />

          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
            </RouterRoutes>
            {/* Global onboarding guide: rendered for VA users so it persists across route navigation */}
            {userProfile?.role === 'va' && (
              <OnboardingGuide userProfile={userProfile} />
            )}
            {/* Global employer onboarding guide: rendered for employer users so it persists across route navigation */}
            {userProfile?.role === 'employer' && (
              <EmployerOnboardingGuide userProfile={userProfile} />
            )}
            <NavigationAnalytics />
            <FloatingChatBubble />
          </NavigationLayout>
        </NavigationProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
